// The ONE place shipping and contact policy lives. Every surface that quotes
// shipping (promo bar, PDP spec line, FAQ, /shipping-returns, discovery set,
// brand pages, meta descriptions, OG cards, the cart) reads from here, so the
// storefront can never promise something the checkout does not charge.
//
// These numbers MUST match the PlazaStack tenant settings (set with
// scripts/set-tenant-shipping.mjs noir-petale --flat 1000 --free-over 20000
// --label "Standard Shipping (3-5 business days)"). Change both together.
// See vault feedback-storefront-claims-must-match-checkout.

export const SHIPPING = {
  /** Flat rate, cents. */
  flatCents:          1000,
  /** Orders whose merchandise total reaches this ship free, cents. */
  freeOverCents:      20000,
  /** Checkout label as the PlazaStack tenant renders it. */
  label:              'Standard Shipping',
  /** Transit window, business days. */
  transitDays:        '3-5',
  /** The only country the checkout ships to. */
  country:            'US',
} as const;

const usd = (cents: number) =>
  `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export const flatShippingLabel     = usd(SHIPPING.flatCents);          // "$10"
export const freeShippingOverLabel = usd(SHIPPING.freeOverCents);      // "$200"

/** Shipping cost for a merchandise subtotal, cents. */
export function shippingCentsFor(subtotalCents: number): number {
  if (subtotalCents <= 0) return 0;
  return subtotalCents >= SHIPPING.freeOverCents ? 0 : SHIPPING.flatCents;
}

/** "Standard Shipping (3-5 business days)". */
export const SHIPPING_SERVICE = `${SHIPPING.label} (${SHIPPING.transitDays} business days)`;

/** Short promo bar line. */
export const PROMO_BAR = `Free U.S. shipping on orders over ${freeShippingOverLabel}`;

/** One-sentence policy, used in descriptions, FAQs and OG cards. */
export const SHIPPING_SENTENCE =
  `Flat ${flatShippingLabel} U.S. shipping, free on orders over ${freeShippingOverLabel}. ` +
  `${SHIPPING.label}, ${SHIPPING.transitDays} business days.`;

/** Compact form for a PDP spec row. */
export const SHIPPING_SPEC = `${flatShippingLabel} flat, free over ${freeShippingOverLabel} · ${SHIPPING.transitDays} business days`;

/** Compact form for OG cards. */
export const SHIPPING_CARD = `Free U.S. shipping over ${freeShippingOverLabel}`;

export const SHIPS_TO = 'We ship within the United States only.';

// ─── Contact fallback (used wherever checkout / forms must not dead-end) ───
export const CONTACT_PHONE = '(351) 322-5409';
export const CONTACT_PHONE_TEL = 'tel:+13513225409';
export const INSTAGRAM_HANDLE = 'noirpetale.co';
export const INSTAGRAM_URL = `https://instagram.com/${INSTAGRAM_HANDLE}`;
export const INSTAGRAM_DM_URL = `https://ig.me/m/${INSTAGRAM_HANDLE}`;

/** Branded fallback sentence for any failed purchase step. */
export const ORDER_FALLBACK =
  `We couldn't complete that just now. You can still order with us directly: ` +
  `call or text ${CONTACT_PHONE}, or message us on Instagram.`;
